// searchAdapters.ts — DTO adapters for the global (⌘K) search endpoint
// (GET /api/search/?q=, PIQ-BACKEND apps/search). The server returns grouped
// hits {products, batches, transfers, prescriptions}, each ≤5 rows plus an
// honest total. Groups surface in SERVED order, and a group the server
// withholds (prescriptions for reference-only roles — patient queries are
// role-gated and audited server-side) simply never renders: the FE never
// synthesises hits, patient or otherwise, from other caches.

import { ukDate } from './stocksAdapters';
import { storeName } from '@/domain/reference';

export interface ProductHitDto {
  id: string;
  snapshot_key: string;
  display_name: string;
  level: string;
}

export interface BatchHitDto {
  id: string;
  batch_number: string;
  sku: string;
  store_id: string;
  expiry_date: string | null;
}

export interface TransferHitDto {
  id: string;
  reference: string;
  status: string;
  source_store_id: string;
  dest_store_id: string;
}

export interface PrescriptionHitDto {
  id: string;
  prescription_id: string;
  status: string;
  /** Blank for reference-only roles — the server only names patients for PH/ADM/SY. */
  patient_name: string;
}

interface SearchGroupDto {
  total: number;
  results: unknown[];
}

export interface SearchResponseDto {
  query: string;
  groups: Record<string, SearchGroupDto | undefined>;
}

export type SearchGroupKey = 'products' | 'batches' | 'transfers' | 'prescriptions';

export interface SearchHit {
  id: string;
  label: string;
  sub: string;
  /** Deep link to the entity's existing page route. */
  to: string;
}

export interface SearchGroupUi {
  key: SearchGroupKey;
  label: string;
  /** Honest server-side match count — hits.length is the ≤5 slice. */
  total: number;
  hits: SearchHit[];
}

const GROUP_LABELS: Record<SearchGroupKey, string> = {
  products: 'Products',
  batches: 'Batches',
  transfers: 'Transfers',
  prescriptions: 'Prescriptions',
};

const HIT_ADAPTERS: Record<SearchGroupKey, (row: unknown) => SearchHit> = {
  products: (row) => {
    const d = row as ProductHitDto;
    return {
      id: d.id,
      label: d.display_name,
      sub: d.snapshot_key,
      to: `/inventory/products/${d.id}`,
    };
  },
  batches: (row) => {
    const d = row as BatchHitDto;
    return {
      id: d.id,
      label: d.batch_number,
      sub: `${d.sku} · exp ${ukDate(d.expiry_date)}`,
      to: `/inventory/batches/${d.id}`,
    };
  },
  transfers: (row) => {
    const d = row as TransferHitDto;
    return {
      id: d.id,
      label: d.reference,
      sub: `${storeName(d.source_store_id)} → ${storeName(d.dest_store_id)} · ${d.status}`,
      // /stocks/transfers consumes ?ref= to open that reference's detail drawer
      to: `/stocks/transfers?ref=${encodeURIComponent(d.reference)}`,
    };
  },
  prescriptions: (row) => {
    const d = row as PrescriptionHitDto;
    return {
      id: d.id,
      label: d.patient_name ? `${d.prescription_id} · ${d.patient_name}` : d.prescription_id,
      sub: d.status,
      to: `/prescriptions/queue/${d.id}`,
    };
  },
};

/** Server groups → UI groups in SERVED order; absent, unknown and empty groups drop out. */
export function searchResponseToUi(dto: SearchResponseDto): SearchGroupUi[] {
  const out: SearchGroupUi[] = [];
  for (const [key, group] of Object.entries(dto?.groups ?? {})) {
    const adapt = HIT_ADAPTERS[key as SearchGroupKey];
    if (!adapt || !group) continue;
    const hits = (group.results ?? []).map(adapt);
    if (hits.length === 0) continue;
    out.push({
      key: key as SearchGroupKey,
      label: GROUP_LABELS[key as SearchGroupKey],
      total: group.total ?? hits.length,
      hits,
    });
  }
  return out;
}
